import { MoreVert } from '@mui/icons-material';
import { ClickAwayListener, IconButton, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GetCustomerSettingsResponse, PositionModel } from '../../../../../Generated/Commodity-Management-Api';
import { ContractDateHelper } from '../../../../../Helpers';
import { ModelToRowTransformer } from '../../../../../Helpers/ModelToRowTransformer';
import { StoneXGrid } from '../../../../StoneX';
import { PositionColumns, PositionReportColumn, PositionReportRow, PositionRow } from './Types';

interface PositionTableProps {
    customerSettings: GetCustomerSettingsResponse;
    positions: PositionModel[];
    columnsToShow: PositionReportColumn[];
    onSetPositionValidation: (dealTicketId: number) => void;
    onEditPosition: (dealTicketId: number) => void;
    onViewAuditTrail: (positionId: number) => void;
    onFilteredPositionsChange?: (filteredPositions: PositionModel[]) => void;
    onCustomDataKeysChange?: (keys: string[]) => void;
}

// Parses a customData JSON string and returns the original-cased key/value pairs.
// Tolerates malformed pseudo-JSON the same way other parsers in the codebase do.
function parseRawCustomDataEntries(customData: unknown): Record<string, string | number | boolean> {
    if (!customData || typeof customData !== 'string' || customData.trim() === '') return {};

    const result: Record<string, string | number | boolean> = {};
    const setEntry = (key: string, value: unknown) => {
        if (!key) return;
        if (typeof value === 'number' || typeof value === 'boolean') {
            result[key] = value;
        } else if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '') return;
            if (!isNaN(Number(trimmed)) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
                result[key] = Number(trimmed);
            } else {
                result[key] = trimmed;
            }
        }
    };

    try {
        const parsed = JSON.parse(customData);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            Object.keys(parsed).forEach(key => setEntry(key, (parsed as Record<string, unknown>)[key]));
            return result;
        }
    } catch {
        // fall through
    }

    const cleaned = customData.replace(/^[\s{]+|[\s}]+$/g, '');
    if (cleaned) {
        cleaned.split(',').forEach(pair => {
            const sepIdx = pair.search(/[;:]/);
            if (sepIdx < 0) return;
            const rawKey = pair.substring(0, sepIdx).trim().replace(/^["']|["']$/g, '');
            const rawVal = pair.substring(sepIdx + 1).trim().replace(/^["']|["']$/g, '');
            setEntry(rawKey, rawVal);
        });
    }
    return result;
}

function humanizeCustomDataKey(key: string): string {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, c => c.toUpperCase())
        .trim();
}

export default function PositionTable(props: PositionTableProps) {
    const { customerSettings, positions, columnsToShow, onSetPositionValidation, onViewAuditTrail, onEditPosition, onCustomDataKeysChange } = props;

    const [columns, setColumns] = useState<ColDef<PositionReportRow>[]>();

    const gridApiRef = useRef<any>(null);

    // Parse each position's customData and collect the union of discovered keys
    // (preserving the order in which they first appear). The parsed entries are
    // also spread onto each row so individual fields are addressable, while the
    // original `_customData` map drives the combined display column.
    const { rowsWithCustomData, customDataKeys } = useMemo(() => {
        const discovered: string[] = [];
        const seen = new Set<string>();
        const rows = positions.map(p => {
            const entries = parseRawCustomDataEntries(p.customData);
            for (const key of Object.keys(entries)) {
                if (!seen.has(key)) {
                    seen.add(key);
                    discovered.push(key);
                }
            }
            return {
                ...p,
                ...ModelToRowTransformer.parseCustomDataField(p.customData),
                _customData: entries,
            };
        });
        return { rowsWithCustomData: rows, customDataKeys: discovered };
    }, [positions]);

    useEffect(() => {
        onCustomDataKeysChange?.(customDataKeys);
    }, [customDataKeys, onCustomDataKeysChange]);

    const handleFilterChanged = () => {
        if (gridApiRef.current && props.onFilteredPositionsChange) {
            const filteredNodes = gridApiRef.current.getModel().rowsToDisplay;
            const filteredPositions = filteredNodes.map((node: any) => node.data);
            props.onFilteredPositionsChange(filteredPositions);
        }
    };

    const onGridReady = (params: any) => {
        gridApiRef.current = params.api;
        handleFilterChanged(); // Initial call with all positions
    };

    function updateColumns() {
        if (!columnsToShow) {
            setColumns([]);
            return;
        }

        const columnDefs = columnsToShow
            .map((column: PositionReportColumn) => {
                const colDef = PositionColumns[column];
                if (!colDef) console.warn(`Missing PositionColumns definition for: ${column}`);
                return colDef as ColDef<PositionReportRow>;
            })
            .filter((col): col is ColDef<PositionReportRow> => col !== undefined);

        if (customDataKeys.length > 0) {
            const buildCustomDataLines = (entries: Record<string, string | number | boolean> | undefined): string[] => {
                if (!entries) return [];
                const lines: string[] = [];
                for (const key of customDataKeys) {
                    const value = entries[key];
                    if (value === undefined || value === null || value === '') continue;
                    lines.push(`${humanizeCustomDataKey(key)}: ${value}`);
                }
                return lines;
            };

            const customDataCol: ColDef<PositionReportRow> = {
                colId: 'customData',
                headerName: 'Custom Data',
                field: '_customData' as any,
                minWidth: 180,
                flex: 1,
                sortable: false,
                filter: 'agTextColumnFilter',
                autoHeight: true,
                wrapText: true,
                cellStyle: { lineHeight: '1.4', paddingTop: 4, paddingBottom: 4, whiteSpace: 'normal' },
                valueGetter: (params) => buildCustomDataLines((params.data as any)?._customData).join('\n'),
                valueFormatter: (params: ValueFormatterParams<PositionReportRow>) =>
                    (params.node?.allChildrenCount ?? 0) > 0 ? '' : (params.value ?? ''),
                cellRenderer: (params: ICellRendererParams<PositionReportRow>) => {
                    if ((params.node?.allChildrenCount ?? 0) > 0) return '';
                    const lines = buildCustomDataLines((params.data as any)?._customData);
                    if (lines.length === 0) return '';
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {lines.map((line, idx) => <span key={idx}>{line}</span>)}
                        </div>
                    );
                },
                tooltipValueGetter: (params) => buildCustomDataLines((params.data as any)?._customData).join('\n'),
            };

            // Insert just before the actions column (if present), otherwise append.
            const actionsIndex = columnDefs.findIndex(col => col.colId === 'actions');
            if (actionsIndex >= 0) {
                columnDefs.splice(actionsIndex, 0, customDataCol);
            } else {
                columnDefs.push(customDataCol);
            }
        }

        if (!columnDefs.some(col => col.colId === 'actions')) {
            columnDefs.push(PositionColumns['Actions'] as ColDef<PositionReportRow>);
        }
        columnDefs.filter(col => col.colId === 'actions').forEach(col => {
            col.cellRenderer = (params: ICellRendererParams<PositionRow>) => {
                const [open, setOpen] = useState(false);
                const anchorRef = useRef<HTMLButtonElement | null>(null);

                return <div>
                    <IconButton ref={anchorRef} sx={{ borderRadius: 1 }} onClick={() => setOpen(true)}><MoreVert /></IconButton>

                    <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" >
                        <ClickAwayListener onClickAway={() => setOpen(false)}>
                            <Paper>
                                <MenuList autoFocusItem={open}>
                                    <MenuItem onClick={() => { props.onSetPositionValidation(params.data!.dealTicketId!); setOpen(false) }}>Set Validation</MenuItem>
                                    <MenuItem onClick={() => { props.onEditPosition(params.data!.dealTicketId!); setOpen(false) }}>Edit</MenuItem>
                                    <MenuItem onClick={() => props.onViewAuditTrail(params.data!.positionId!)}>Audit</MenuItem>
                                </MenuList>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </div>
            }
        });

        setColumns(columnDefs);
    }

    const gridOptions: AgGridReactProps = useMemo(() => {
        return {
            rowGroupPanelShow: 'never',
            enableRangeSelection: true,
            groupDisplayType: "groupRows",
            defaultColDef: {
                sortable: true,
                resizable: true,
            },
            groupRowRendererParams: {
                suppressCount: true,
                comparator: (valueA: any, valueB: any) => {
                    if (valueA == null) return 1;
                    if (valueB == null) return -1;
                    return ContractDateHelper.sortAscending(valueA, valueB);
                },
                innerRenderer: (params: any) => {
                    const shortMonthYear = params.value;
                    const date = dayjs(shortMonthYear, "MMM YY");
                    return date.isValid() ? date.format("MMMM YYYY") : shortMonthYear;
                },
            },
            onGridReady: onGridReady,
            onFilterChanged: handleFilterChanged,
        };
    }, []);

    useEffect(updateColumns, [columnsToShow, customDataKeys])
    return (
        <StoneXGrid
            rows={rowsWithCustomData}
            columns={columns!}
            gridOptions={gridOptions}
            getRowId={x => x.data.positionId}
            onlyShowGroupLabels />

    );
}